import { useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
  Field,
  Button,
  UrlInput,
  Link,
  Text,
  Stack,
  Tooltip,
  tokens,
  IconButton,
  useNotify,
} from '@hotjar/hotsauce';
import type { WebhooksSettings } from '@hotjar/integrations/domains/webhooks/api';
import { validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { sendWebhookTestMessage } from '@hotjar/integrations';
import type { Site } from '@hotjar/site';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { RemoveWebhookModal } from './RemoveWebhookModal';

const WebhookInput = styled(UrlInput)`
  min-width: 300px;
`;

const ConnectedChannelsHeader = styled(Text)`
  display: block;
  margin-top: ${tokens.spacer.x2};
  margin-bottom: ${tokens.spacer.x1};
`;

const NoChannelsConnectedText = styled(Text)`
  display: block;
`;

const WebhookContainer = styled(Stack)`
  align-items: center;
  min-width: 0;
  width: 100%;
  justify-content: space-between;
`;

const WebhookUrl = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface ForwardToWebhooksInputProps {
  settings: WebhooksSettings;
  onChange: (settings: Partial<WebhooksSettings>) => void;
  currentSite: Site | undefined;
}

export function ForwardToWebhooksInput({
  settings,
  onChange,
  currentSite,
}: ForwardToWebhooksInputProps): JSX.Element {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const [currentWebhook, setCurrentWebhook] = useState('');
  const [isCurrentWebhookValid, setCurrentWebhookValid] = useState(true);
  const [isTestWebhookLoading, setIsTestWebhookLoading] = useState(false);
  const [webhookTest, setWebhookTest] = useState('');
  const [webhookToRemove, setWebhookToDelete] = useState('');
  const { showToast } = useNotify();
  const inputEl = useRef<HTMLInputElement>(null);

  const onChangeWebhooks = (webhooks: string[]): void => {
    onChange({ webhooks, valid: webhooks.length > 0, dirty: true });
  };

  const onWebhookConfirmClick = (): void => {
    if (!currentWebhook || !isCurrentWebhookValid) {
      return;
    }
    if (!settings.webhooks) {
      onChangeWebhooks([currentWebhook]);
    } else if (!settings.webhooks.includes(currentWebhook)) {
      onChangeWebhooks([...settings.webhooks, currentWebhook]);
    }
    if (inputEl.current) {
      inputEl.current.value = '';
      setCurrentWebhook('');
    }
  };

  const onWebhookKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      onWebhookConfirmClick();
    }
  };

  const onWebhookValueChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setCurrentWebhook(event.target.value);
    setCurrentWebhookValid(validateHTTPSUrl(event.target.value));
    onChange({ dirty: true });
  };

  const onWebhookSendTestMessage = async (webhook: string) => {
    setIsTestWebhookLoading(true);
    setWebhookTest(webhook);
    try {
      await sendWebhookTestMessage(`${currentSite?.id}`, webhook);
      showToast({
        kind: 'success',
        text: t('common:forwarding.webhooks.webhook-test-message-success'),
      });
    } catch (error) {
      showToast({
        kind: 'danger',
        text: t('common:forwarding.webhooks.webhook-test-message-error'),
      });
    } finally {
      setIsTestWebhookLoading(false);
      setWebhookTest('');
    }
  };

  const onWebhookRemoveConfirmation = (): void => {
    onChangeWebhooks((settings.webhooks as string[]).filter((w) => w !== webhookToRemove));
    setWebhookToDelete('');
  };

  const WebhookInputHelperText = (
    <Trans i18nKey="common:forwarding.webhooks.webhooks-helper-text">
      Learn how with this guide
      {/* TODO: update the help URL */}
      <Link kind="inline" href={`${helpHotjarUrl}`} isExternal>
        using Hotjar with Webhooks
      </Link>
    </Trans>
  );

  return (
    <>
      {webhookToRemove && (
        <RemoveWebhookModal
          onConfirm={() => onWebhookRemoveConfirmation()}
          onCancel={() => setWebhookToDelete('')}
        />
      )}

      <Field
        labelText={t('common:forwarding.webhooks.webhooks-label')}
        helperText={WebhookInputHelperText}
        inputId="survey-webhooks-input"
        size="l"
      >
        <Stack spacing="x1">
          <WebhookInput
            id="survey-webhooks-input"
            placeholder={t('common:forwarding.webhooks.webhooks-input-placeholder')}
            onChange={onWebhookValueChange}
            onKeyDown={onWebhookKeyDown}
            ref={inputEl}
            isInvalid={!isCurrentWebhookValid}
          />
          <Button
            onClick={onWebhookConfirmClick}
            isDisabled={!currentWebhook || !isCurrentWebhookValid}
          >
            {t('common:forwarding.webhooks.webhooks-button-confirm')}
          </Button>
        </Stack>
      </Field>

      <ConnectedChannelsHeader textType="subtitle2">
        {t('common:forwarding.webhooks.connected-webhooks')}
      </ConnectedChannelsHeader>
      <Stack spacing="x1" direction="column">
        {(!settings.webhooks || settings.webhooks?.length === 0) && (
          <NoChannelsConnectedText textColor="text_disabled">
            {t('common:forwarding.webhooks.no-url-connected')}
          </NoChannelsConnectedText>
        )}
        {settings.dirty && !settings.valid && (
          <Text textColor="text_error">{t('common:forwarding.webhooks.webhooks-error')}</Text>
        )}
        {settings.webhooks?.map((webhook) => (
          <WebhookContainer spacing="x1" key={webhook}>
            <Tooltip text={webhook}>
              <WebhookUrl>{webhook}</WebhookUrl>
            </Tooltip>
            <Stack spacing="x1">
              <Button
                kind="secondary"
                size="s"
                onClick={() => onWebhookSendTestMessage(webhook)}
                isLoading={isTestWebhookLoading && webhook === webhookTest}
              >
                {t('common:forwarding.webhooks.webhook-send-test-message')}
              </Button>
              <IconButton
                icon="trash"
                size="s"
                description={t('common:forwarding.webhooks.webhook-remove')}
                tooltipText={t('common:forwarding.webhooks.webhook-remove')}
                onClick={() => setWebhookToDelete(webhook)}
              />
            </Stack>
          </WebhookContainer>
        ))}
      </Stack>
    </>
  );
}
